import axios from 'axios';
import { StateCreator } from 'zustand';
import { uploadPatientDocument, uploadTaskDocument } from '../../services/api';
import type { PatientDocumentResponse } from '../../services/api/types.gen';
// Type-only import — erased at compile time, so no runtime circular dependency
// with store/index.ts. Gives this slice's get()/set() the full store type,
// which it needs to drive the toast slice (addToast/updateToast).
import type { StoreState } from '../index';

interface StartTaskDocumentUploadArgs {
  taskId: string;
  file: File;
  // Human-readable target ("{task} · {patient}") shown in the toast. Falls back
  // to a generic label when a caller can't supply one.
  targetLabel?: string;
  displayName?: string;
  // Owning patient. When supplied, a successful task upload also bumps
  // patientUploadTick[patientId], so the patient page's document-count badge
  // refreshes even though the upload targeted a task — patientUploadTick is the
  // canonical "a document changed for this patient" signal.
  patientId?: string;
}

interface StartPatientDocumentUploadArgs {
  patientId: string;
  file: File;
  targetLabel?: string;
  displayName?: string;
}

export interface UploadsSlice {
  // Per-task monotonic counter, bumped on each successful task-document upload.
  // Mounted popovers watch their own taskId's tick to refresh after a
  // background upload completes.
  taskUploadTick: Record<string, number>;
  // Per-task count of uploads still in flight. Task-document uploads are
  // fire-and-forget, so a surface that signs the task needs to know one is
  // outstanding: signing mid-upload would either snapshot without the document
  // or lock the task just before the upload lands. Keys are removed at zero so
  // the record does not grow for every task ever touched.
  taskUploadsInFlight: Record<string, number>;
  // Per-patient monotonic counter, bumped on each successful patient-scoped
  // upload. The Documents tab watches its patientId's tick to refresh — and
  // survives the user navigating away and back mid-upload.
  patientUploadTick: Record<string, number>;
  // Kicks off the upload in the background, drives a persistent toast to
  // success/failure, and resolves for the caller. Resolves the uploaded
  // `PatientDocumentResponse` when the document landed (including the
  // finished-before-cancel race), and `null` on failure OR a clean cancel
  // (never rejects). Callers that just want the background behavior (the
  // popover) ignore the result; the Documents-tab viewer awaits the doc to
  // swap its optimistic preview for the persisted doc and keep it focused,
  // and drops the preview when the result is null.
  startTaskDocumentUpload: (
    args: StartTaskDocumentUploadArgs
  ) => Promise<PatientDocumentResponse | null>;
  startPatientDocumentUpload: (
    args: StartPatientDocumentUploadArgs
  ) => Promise<PatientDocumentResponse | null>;
  // Aborts the in-flight upload behind a loading toast and dismisses it. No-op
  // if the upload already settled (its controller is gone). Whether the abort
  // actually prevents the document from landing is a race the server resolves —
  // see the finished-before-cancel handling in runBackgroundUpload.
  cancelUpload: (toastId: string) => void;
}

// Options threaded into the axios RequestConfig of the underlying upload call.
// `signal` lets `cancelUpload` abort the request; `onUploadProgress` drives the
// determinate bar as bytes reach OUR server (not S3 — the S3 put happens after
// the last byte lands, which is why 100% flips to an indeterminate "Finalizing…"
// rather than "done").
interface UploadCallOptions {
  signal: AbortSignal;
  onUploadProgress: (event: { loaded: number; total?: number }) => void;
}

// Per-in-flight-upload abort handle, keyed by the loading toast's id. Held at
// module scope (not slice state) because it carries a live AbortController — a
// non-serializable object that has no business in the reactive store. The
// `cancelRequested` flag records that the user asked to cancel, so the success
// branch can tell a clean upload apart from one that finished-despite-cancel.
const uploadControllers = new Map<
  string,
  { controller: AbortController; cancelRequested: boolean }
>();

// Shared toast lifecycle for a background document upload: one persistent
// "Uploading…" toast that morphs to success in place, or is replaced by a fresh
// (screen-reader-announced) persistent error toast on failure. The caller
// supplies the actual network call and the success side effect (tick bump).
async function runBackgroundUpload(
  get: () => StoreState,
  {
    file,
    target,
    patientId,
    doUpload,
    onSuccess,
  }: {
    file: File;
    target: string;
    // Owning patient, when known. Used only to deep-link the
    // finished-before-cancel notice at the document that landed anyway.
    patientId?: string;
    doUpload: (
      opts: UploadCallOptions
    ) => Promise<{ data?: PatientDocumentResponse } | undefined>;
    onSuccess: () => void;
  }
): Promise<PatientDocumentResponse | null> {
  const description = `${file.name} → ${target}`;

  const toastId = get().addToast({
    title: 'Uploading document',
    description,
    loading: true,
    progress: 0,
    cancellable: true,
  });

  const controller = new AbortController();
  const entry = { controller, cancelRequested: false };
  uploadControllers.set(toastId, entry);

  // Progress reflects bytes reaching OUR server, not S3. When the last byte
  // lands (p >= 1) the request is still pending while the server does its S3
  // put + DB commit, so we drop the determinate bar and the Cancel action and
  // switch to an indeterminate "Finalizing…" — a full bar must never imply the
  // document is persisted, and cancel can no longer be honored past this point.
  const onUploadProgress = (event: { loaded: number; total?: number }) => {
    const p = event.total ? event.loaded / event.total : undefined;
    if (p != null && p >= 1) {
      get().updateToast(toastId, {
        title: 'Finalizing…',
        progress: undefined,
        cancellable: false,
      });
    } else if (p != null) {
      get().updateToast(toastId, { progress: p });
    } else {
      // total is 0 or absent (non-length-computable / empty upload): clear the
      // determinate bar so it doesn't freeze at 0%. The spinner keeps conveying
      // activity; Cancel stays available since the upload is still in flight.
      get().updateToast(toastId, { progress: undefined });
    }
  };

  try {
    const result = await doUpload({
      signal: controller.signal,
      onUploadProgress,
    });
    const uploadedDoc = result?.data ?? null;

    if (entry.cancelRequested) {
      // The last-moment cancel race (EMR-542): the user hit Cancel during the
      // upload leg, but the server received the full body and committed (S3 put
      // + DB insert) before the abort took effect. The document LANDED. We
      // cannot undo a completed server-side upload from the client, and
      // silently dropping the toast would strand an orphaned clinical document
      // the clinician believes they discarded. So we tell the truth: bump the
      // tick (the doc is real and must show in lists/badge) and surface a
      // distinct notice with a deep link to go delete it if unwanted. A fresh
      // toast (not an in-place patch) so screen readers announce it.
      onSuccess();
      get().removeToast(toastId);
      get().addToast({
        title: 'Uploaded before cancel',
        description: `'${file.name}' finished uploading before it could be canceled. Delete it manually if it shouldn't be here.`,
        variant: 'destructive',
        duration: null,
        ...(patientId
          ? {
              linkAction: {
                label: 'View document',
                altText: 'View documents for this patient',
                href: `/patients/${patientId}/documents`,
              },
            }
          : {}),
      });
      // The doc landed despite the cancel — return it so the viewer shows it.
      // The notice above already tells the user to delete it if unwanted.
      return uploadedDoc;
    }

    // Success morphs the same toast in place (spinner → done): one operation
    // with a lifecycle, not a new message. Success carries no a11y regression —
    // the old flow surfaced no success toast at all. Clear progress/cancellable
    // belt-and-suspenders so a partial bar or live Cancel can't cling to the
    // green toast if the terminal progress event never arrived.
    get().updateToast(toastId, {
      title: 'Document uploaded',
      description,
      variant: 'default',
      loading: false,
      progress: undefined,
      cancellable: false,
      duration: 4000,
    });
    onSuccess();
    return uploadedDoc;
  } catch (err) {
    if (axios.isCancel(err)) {
      // Clean cancel: the abort landed before the server committed (it saw a
      // truncated body → no document). Remove the loading toast, no tick, no
      // error toast. NOTE: a CanceledError where the server nonetheless
      // committed is NOT client-detectable without an idempotency/verification
      // handle — that residual is EMR-542. We do not fabricate certainty here;
      // the detectable finished-despite-cancel case is handled in the resolve
      // branch above.
      get().removeToast(toastId);
      return null;
    }
    console.error('Background document upload failed', err);
    // A failure must reach the user — including screen-reader users. Radix only
    // announces a toast on mount, not on an in-place update, so we replace the
    // loading toast with a FRESH one (new mount → announced), matching the old
    // flow's fresh showError toast.
    get().removeToast(toastId);
    get().addToast({
      title: 'Upload failed',
      description: `Couldn't upload ${file.name} to ${target}. Please try again.`,
      variant: 'destructive',
      // Persist a background failure until the user dismisses it — they may have
      // navigated away and shouldn't miss it.
      duration: null,
    });
    return null;
  } finally {
    uploadControllers.delete(toastId);
  }
}

export const uploadsSlice: StateCreator<StoreState, [], [], UploadsSlice> = (
  set,
  get
) => ({
  taskUploadTick: {},
  taskUploadsInFlight: {},
  patientUploadTick: {},

  startTaskDocumentUpload: ({
    taskId,
    file,
    targetLabel,
    displayName,
    patientId,
  }) => {
    const adjustInFlight = (delta: number) =>
      set(state => {
        const next = (state.taskUploadsInFlight[taskId] ?? 0) + delta;
        const remaining = { ...state.taskUploadsInFlight };
        if (next > 0) {
          remaining[taskId] = next;
        } else {
          delete remaining[taskId];
        }
        return { taskUploadsInFlight: remaining };
      });

    adjustInFlight(1);
    // Decrement on every outcome — success, failure and cancel alike — so a
    // failed upload cannot leave the task permanently unsignable.
    // runBackgroundUpload never rejects, and its onSuccess tick bump runs before
    // this, so by the time the count reaches zero the refresh is already queued.
    return runBackgroundUpload(get, {
      file,
      target: targetLabel ?? 'this task',
      patientId,
      doUpload: ({ signal, onUploadProgress }) =>
        uploadTaskDocument({
          path: { task_id: taskId },
          ...(displayName ? { query: { display_name: displayName } } : {}),
          body: { file },
          // signal + onUploadProgress are axios RequestConfig fields; the
          // generated sdk spreads ...options into axios, so they pass through
          // to drive cancel and the progress bar.
          signal,
          onUploadProgress,
          // The generated client does NOT throw by default; without this a
          // failed upload would fall through and render as a success toast.
          throwOnError: true,
        }),
      onSuccess: () =>
        set(state => ({
          taskUploadTick: {
            ...state.taskUploadTick,
            [taskId]: (state.taskUploadTick[taskId] ?? 0) + 1,
          },
          // Also bump the patient signal so the patient page's Documents badge
          // refreshes — the upload targeted a task but added a patient document.
          ...(patientId
            ? {
                patientUploadTick: {
                  ...state.patientUploadTick,
                  [patientId]: (state.patientUploadTick[patientId] ?? 0) + 1,
                },
              }
            : {}),
        })),
    }).finally(() => adjustInFlight(-1));
  },

  startPatientDocumentUpload: ({ patientId, file, targetLabel, displayName }) =>
    runBackgroundUpload(get, {
      file,
      target: targetLabel ?? 'this patient',
      patientId,
      doUpload: ({ signal, onUploadProgress }) =>
        uploadPatientDocument({
          path: { patient_id: patientId },
          ...(displayName ? { query: { display_name: displayName } } : {}),
          body: { file },
          signal,
          onUploadProgress,
          throwOnError: true,
        }),
      onSuccess: () =>
        set(state => ({
          patientUploadTick: {
            ...state.patientUploadTick,
            [patientId]: (state.patientUploadTick[patientId] ?? 0) + 1,
          },
        })),
    }),

  cancelUpload: (toastId: string) => {
    const entry = uploadControllers.get(toastId);
    // Guard a missing controller: double-cancel, or a Cancel click that raced
    // just past the upload settling (finally already deleted the entry).
    if (!entry) return;
    // Record intent BEFORE aborting so the resolve branch can distinguish a
    // finished-despite-cancel upload from a clean one, then abort and dismiss
    // the loading toast. The request's own reject/resolve does the rest of the
    // cleanup (uploadControllers.delete runs in runBackgroundUpload's finally).
    entry.cancelRequested = true;
    entry.controller.abort();
    get().removeToast(toastId);
  },
});
